import React, { useState } from "react";
import { useOutletContext } from "react-router-dom";
import CoreDefault from "@/core/Defaults";
// components
import RMDRImage from "@/components/RMDRImage";
import RMDRLink from "@/components/RMDRLink";
import NoData from "@/components/NoData";
import { HiOutlineNewspaper } from "react-icons/hi2";
import ContentMetricsString from "@/components/ContentMetrics/ContentMetricsString";
// types
import { TMainPageLayoutV3 } from "../type";
import TOutletContext from "@/cms/types/TOutletContext";
import TClientContentList from "@/cms/types/TClientContentList";
import TApplicationDocument from "@/cms/types/TApplicationDocument";
import TClientMainPageItemList from "@/cms/types/TClientMainPageItemList";
// utilities
import { 
    metaDataLanguageVersion, 
    pageNameDescriptionLanguageVersion, 
    redirectUrl 
} from "@/utilities/LanguageVersionSelectorShortcut";
import { dateTimeToHumanReadable } from "@/utilities/DateTimeHelper";
import ApplicationDocumentSelector from "@/utilities/ApplicationDocumentSelector";
// enums
import EResourceType from "@/cms/enums/EResourceType";
import RMDRComponentSanitizer from "@/components/RMDRComponentSanitizer";


type TMNPG014 = {
    params:{
        payloadList?:TClientContentList[];
        payloadListIsLoading?:boolean;
        payloadListCount?:number;
        [key:string]:any;
    }
}

type TRenderItems = {
    payloadList?:TClientContentList[];
    webApplicationLanguageCode:string;
    readMoreString:string;
    template?:TClientMainPageItemList;
}

const RenderItems = ( props:TRenderItems ) => {
    return props.payloadList?.map((item, index) => {
        const itemLanguageVersion = pageNameDescriptionLanguageVersion(item?.languageVersions);
        const _redirectUrl = redirectUrl(item.languageVersions);
        const dateTimeHR = dateTimeToHumanReadable(item?.creationDateTime, props.webApplicationLanguageCode);
        
        return (
            <div
                className="col-xl-4 col-lg-6 col-md-6"
                data-aos-duration="800"
                data-aos="fade-up"
                data-aos-delay={300+((index%3)*200)}
            >
                <div className="news-card-items mt-0">
                    <div className="news-content">
                        <h3>
                            <RMDRLink
                                text={itemLanguageVersion?.name}
                                title={itemLanguageVersion?.name}
                                hasNavigationDestination={props.template?.hasDestinationNavigation}
                                navigationURL={itemLanguageVersion?.contentRouteNavigationURL}
                                redirectType={props.template?.redirectType}
                                redirectUrl={_redirectUrl?.value}
                                className="tw-line-clamp-1 news-details"
                                isShowAlways={true}
                            />
                        </h3>
                        <ul>
                            <li>
                                <i className="fas fa-calendar-alt"></i>
                                <time dateTime={item.creationDateTime}>{dateTimeHR}</time>
                            </li>
                            <li>
                                <ContentMetricsString
                                    contentId={item.id}
                                    className="d-inline-flex gap-2 flex-grow-1 justify-content-end"
                                />
                            </li>
                        </ul>
                        <RMDRComponentSanitizer
                            languageVersion={itemLanguageVersion}
                            variant="p"
                            valueExpr="value"
                            style={{height: "56px"}}
                            className="tw-line-clamp-2"
                            removeTag
                            showAlways
                        />
                        <RMDRLink
                            title={itemLanguageVersion?.name}
                            hasNavigationDestination={props.template?.hasDestinationNavigation}
                            navigationURL={itemLanguageVersion?.contentRouteNavigationURL}
                            redirectType={props.template?.redirectType}
                            redirectUrl={_redirectUrl?.value}
                            className="theme-btn-2 mt-3"
                            isShowAlways={true}
                        >
                            {props.readMoreString}
                            <i className="fas fa-long-arrow-right"></i>
                        </RMDRLink>
                    </div>
                </div>
            </div>
        )
    })
}

const LoadingSkeleton = () => {
    return (
        <>
            {
                Array.from({ length:6 }).map(() => (
                    <div className="col-xl-4 col-lg-6 col-md-6 tw-animate-pulse">
                        <div className="tw-h-96 tw-bg-gray-100" />
                    </div>
                ))
            }
        </>
    )
}

const MNPG014 = ( props: TMainPageLayoutV3<TMNPG014> ) => {
    console.log("MNPG014.rendered: ", props);

    const { siteSettingPayload, webApplicationLanguageCode } = useOutletContext<TOutletContext>();
    const { params } = props;
    const { payloadList, payloadListIsLoading=true, payloadListCount=0 } = params;
    const [ readMoreString ] = useState(metaDataLanguageVersion("readMoreString", siteSettingPayload.languageVersions));

    return (
        <div className="row g-4">
            {
                payloadListIsLoading && <LoadingSkeleton />
            }
            {
                !payloadListIsLoading && payloadList && payloadList?.length > 0 && payloadListCount > 0 && (
                    <RenderItems
                        readMoreString={readMoreString}
                        webApplicationLanguageCode={webApplicationLanguageCode}
                        payloadList={payloadList}
                        template={props.template}
                    />
                )
            }
            
            {
                !payloadListIsLoading && payloadList?.length === 0 && payloadListCount === 0 && (
                    <NoData
                        icon={<HiOutlineNewspaper className="tw-h-40 tw-stroke-1 tw-text-9xl" />}
                        className="tw-flex tw-flex-col tw-items-center tw-justify-center"
                        textClassName="tw-text-center tw-text-xl"
                        languageVersions={siteSettingPayload.languageVersions}
                    />
                )
            }
        </div>
        
    )
}

const Component = React.memo(MNPG014, ( prevProps:TMainPageLayoutV3<TMNPG014>, nextProps:TMainPageLayoutV3<TMNPG014> ) => {
    return !(
        prevProps.params?.payloadList !== nextProps.params?.payloadList
        || prevProps.params?.payloadListIsLoading !== nextProps.params?.payloadListIsLoading
        || prevProps.params?.payloadListCount !== nextProps.params?.payloadListCount
    );
});

export default Component;
